import * as t from 'io-ts';

import {
  RAnalyticsSavedViewFiltersDirty,
  RAnalyticsSavedViewMetadataDirty,
  RSchedulingConfigDirty,
} from '../../../../models/api/analyticsSavedViewModel';

/*
 * Constants.
 */

export const ALL_DATA_ID = 'ALL_DATA';
export const TEMPORARY_VIEW_ID_PREFIX = 'TEMPORARY_VIEW';

/*
 * Runtime types.
 */

const RAnalyticsSelectedViewIdDirty = t.union([t.number, t.literal(ALL_DATA_ID), t.string, t.undefined]);

const RAnalyticsViewStateDirty = t.type({
  id: t.union([t.number, t.string]),
  name: t.string,
  isPinned: t.boolean,
  isDisplayed: t.boolean,
  // Explanation of visibility namespace vs. namespace:
  // https://paper.dropbox.com/doc/BgVQW2dMnIQCjBTvzAX82~i9Ag-VFKE8bAl6ComWco9U1PM2
  visibilityNamespace: t.union([t.string, t.undefined]),
  namespace: t.union([t.string, t.undefined]),
  metadata: RAnalyticsSavedViewMetadataDirty,
  filters: RAnalyticsSavedViewFiltersDirty,
  isDefault: t.boolean,
  schedulingConfig: t.union([RSchedulingConfigDirty, t.null]),
});

export const RAnalyticsPanelViewsStateDirty = t.intersection([
  t.type({
    selectedViewId: RAnalyticsSelectedViewIdDirty,
    isFetchingSavedViews: t.boolean,
    savedViews: t.readonlyArray(RAnalyticsViewStateDirty),
    temporaryViews: t.readonlyArray(RAnalyticsViewStateDirty),
    nbTemporaryViews: t.number,
  }),
  t.partial({
    error: t.number,
  }),
]);

/*
 * Types.
 */

export type AnalyticsSelectedViewId = t.TypeOf<typeof RAnalyticsSelectedViewIdDirty>;

export interface AnalyticsSavedView extends Readonly<t.TypeOf<typeof RAnalyticsViewStateDirty>> {}

export interface AnalyticsPanelViewsState extends Readonly<t.TypeOf<typeof RAnalyticsPanelViewsStateDirty>> {}

/*
 * Type guards.
 */

export function isSelectedViewAllData(
  savedViewId: AnalyticsSelectedViewId,
): savedViewId is typeof ALL_DATA_ID {
  return savedViewId === ALL_DATA_ID;
}

export function isAnalyticsViewTemporary(viewId: AnalyticsSelectedViewId): viewId is string {
  return typeof viewId === 'string' && viewId.startsWith(TEMPORARY_VIEW_ID_PREFIX);
}

export function isAnalyticsViewSaved(viewId: AnalyticsSelectedViewId): viewId is number {
  return !isSelectedViewAllData(viewId) && !isAnalyticsViewTemporary(viewId);
}

/*
 * Helpers.
 */

// This function is here rather than the reducer to fix a circular dep issue.
export function makeAnalyticsPanelViewsState(): AnalyticsPanelViewsState {
  return {
    selectedViewId: ALL_DATA_ID,
    isFetchingSavedViews: false,
    savedViews: [],
    temporaryViews: [],
    nbTemporaryViews: 0,
  };
}
